import { GOAL_RAFFLE_TYPE } from "react-ui-components/components/models/IRaffleGoal"
import {
  normalizeRaffleGoalModeFields,
} from "react-ui-components/components/raffle/raffleGoalUtils"
import {
  getDirectRaffleMediaItems,
  syncLegacyRaffleMediaFields,
} from "react-ui-components/components/raffle/raffleMediaUtils"

import { validateWeeklySchedule } from "./raffleSchedule"

const GOAL_TYPE = GOAL_RAFFLE_TYPE.toLowerCase()
const VALID_PROGRESS_DISPLAY_MODES = new Set(["count", "percentage", "both"])
const ZIP_CODE_PATTERN = /^\d{5}$/

export const ALLOWED_ZIP_HELP_TEXT =
  "Opcional. Si introduces un ZIP Code, la publicación estará limitada a esa zona. Si lo dejas vacío, utilizará automáticamente la localidad principal asignada."

export const normalizeAllowedZipForSave = (allowedZip: unknown): string =>
  typeof allowedZip === "string" ? allowedZip.trim() : ""

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === "[object Object]"

const toFiniteNumber = (value: unknown, fallback: number) => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : fallback
}

const toPositiveInteger = (value: unknown, fallback: number) => {
  const numberValue = Math.floor(toFiniteNumber(value, fallback))
  return numberValue > 0 ? numberValue : fallback
}

const toNonNegativeInteger = (value: unknown, fallback = 0) => {
  const numberValue = Math.floor(toFiniteNumber(value, fallback))
  return numberValue >= 0 ? numberValue : fallback
}

const optionalString = (value: unknown) => (typeof value === "string" && value.trim() ? value : undefined)
const optionalTrimmedString = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined

const optionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return undefined
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : undefined
}

const optionalPositiveInteger = (value: unknown) => {
  const numberValue = optionalNumber(value)
  if (numberValue === undefined) return undefined

  const integerValue = Math.floor(numberValue)
  return integerValue > 0 ? integerValue : undefined
}

const optionalOpacity = (value: unknown) => {
  const numberValue = optionalNumber(value)
  return numberValue !== undefined && numberValue >= 0 && numberValue <= 1
    ? numberValue
    : undefined
}

const optionalDateMillis = (value: unknown) => {
  if (value === null || value === undefined || value === "") return undefined
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (value instanceof Date) return value.getTime()
  if (typeof value === "string") {
    const millis = new Date(value).getTime()
    return Number.isFinite(millis) ? millis : undefined
  }
  return undefined
}

export const normalizeTicketDesignForSave = (ticketDesign: unknown) => {
  if (!isPlainObject(ticketDesign)) return undefined

  const cleaned: Record<string, unknown> = {}
  Object.entries(ticketDesign).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (typeof value === "string" && !value.trim()) return
    if (typeof value === "number" && !Number.isFinite(value)) return

    cleaned[key] = typeof value === "string" ? value.trim() : value
  })

  return Object.keys(cleaned).length ? cleaned : undefined
}

const cleanObject = (value: unknown): unknown => {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined
  if (value instanceof Date) return value.getTime()
  if (Array.isArray(value)) {
    const cleanedArray = value.map(cleanObject).filter((item) => item !== undefined)
    return cleanedArray.length ? cleanedArray : undefined
  }
  if (!isPlainObject(value)) return value

  const cleaned: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, entryValue]) => {
    const nextValue = cleanObject(entryValue)
    if (nextValue !== undefined) {
      cleaned[key] = nextValue
    }
  })

  return Object.keys(cleaned).length ? cleaned : undefined
}

export const isGoalRaffleFormData = (raffle: any) => {
  if (!raffle) return false
  if (String(raffle.type || "").trim().toLowerCase() === GOAL_TYPE) return true
  if (raffle.raffleMode === "goal") return true

  const hasExplicitNonGoalType =
    typeof raffle.type === "string" &&
    raffle.type.trim().length > 0 &&
    raffle.type.trim().toLowerCase() !== GOAL_TYPE

  return !hasExplicitNonGoalType && Boolean(raffle.goalSettings)
}

export const normalizeRaffleForForm = (raffle: any) => {
  if (!raffle) return raffle
  const normalizedRaffle = normalizeRaffleGoalModeFields(raffle, {
    seedGoalSettings: isGoalRaffleFormData(raffle),
  }) || raffle

  // RJSF's BaseInputTemplate converts a cleared string input ("") into `options.emptyValue`
  // (`undefined` by default). Coercing non-string values back to `""` keeps the controlled
  // form field empty in React state and ensures subsequent saves persist `""` instead of
  // omitting `allowedZip` from `updateDoc`.
  const allowedZip =
    typeof normalizedRaffle.allowedZip === "string" ? normalizedRaffle.allowedZip : ""

  if (!Array.isArray(normalizedRaffle.raffleMediaItems) || !normalizedRaffle.raffleMediaItems.length) {
    return {
      ...normalizedRaffle,
      allowedZip,
      raffleMediaItems: getDirectRaffleMediaItems(normalizedRaffle),
    }
  }

  return {
    ...normalizedRaffle,
    allowedZip,
  }
}

export const normalizeGoalSettingsForSave = (goalSettings: any) => {
  const source = isPlainObject(goalSettings) ? goalSettings : {}
  const metricsSource = isPlainObject(source.metrics) ? source.metrics : {}
  const metrics = cleanObject({
    interactionTotal: optionalNumber(metricsSource.interactionTotal),
    uniqueParticipants: optionalNumber(metricsSource.uniqueParticipants),
    lastInteractionAt: optionalDateMillis(metricsSource.lastInteractionAt),
    goalReachedAt: optionalDateMillis(metricsSource.goalReachedAt),
  })

  return cleanObject({
    targetCount: toPositiveInteger(source.targetCount, 50),
    currentCount: toNonNegativeInteger(source.currentCount, 0),
    incrementStep: toPositiveInteger(source.incrementStep, 1),
    actionLabel: optionalString(source.actionLabel),
    actionIconAsset: optionalString(source.actionIconAsset),
    lockedTitle: optionalString(source.lockedTitle),
    lockedDescription: optionalString(source.lockedDescription),
    unlockedTitle: optionalString(source.unlockedTitle),
    unlockedDescription: optionalString(source.unlockedDescription),
    rewardTitle: optionalString(source.rewardTitle),
    rewardDescription: optionalString(source.rewardDescription),
    backgroundColor: optionalTrimmedString(source.backgroundColor),
    backgroundOpacity: optionalOpacity(source.backgroundOpacity),
    showProgressBar: source.showProgressBar ?? true,
    progressLabel: optionalString(source.progressLabel),
    progressDisplayMode: VALID_PROGRESS_DISPLAY_MODES.has(String(source.progressDisplayMode))
      ? source.progressDisplayMode
      : "both",
    progressBarColor: optionalString(source.progressBarColor),
    progressTrackColor: optionalString(source.progressTrackColor),
    maxClicksPerUser:
      source.maxClicksPerUser === null || source.maxClicksPerUser === ""
        ? null
        : optionalPositiveInteger(source.maxClicksPerUser),
    overflowAllowed: Boolean(source.overflowAllowed),
    metrics,
  })
}

export const normalizePublishNotificationForSave = (publishNotification: unknown) => {
  const source = isPlainObject(publishNotification) ? publishNotification : {}
  const sendOnPublish = Boolean(
    source.sendOnPublish === true || source.enabled === true || source.send === true,
  )

  return cleanObject({
    sendOnPublish,
    enabled: sendOnPublish,
    titleKey: optionalTrimmedString(source.titleKey),
    bodyKey: optionalTrimmedString(source.bodyKey),
    title: optionalTrimmedString(source.title),
    body: optionalTrimmedString(source.body),
    imageUrl: optionalTrimmedString(source.imageUrl),
    status: optionalTrimmedString(source.status),
    sentAt: source.sentAt,
    sentByEventId: optionalTrimmedString(source.sentByEventId),
    successCount: optionalNumber(source.successCount),
    failureCount: optionalNumber(source.failureCount),
  })
}

const defaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

export const normalizeTimerForSave = (timer: unknown) => {
  if (!isPlainObject(timer)) return undefined

  const enabled = Boolean(timer.enabled)
  const startAt = optionalDateMillis(timer.startAt)
  const endAt = optionalDateMillis(timer.endAt)
  const timezone = optionalTrimmedString(timer.timezone) || defaultTimezone()
  const endAction = timer.endAction === "none" ? "none" : "disable_raffle"

  // Nothing meaningfully configured (a raffle that never touched the timer form section) -
  // omit the field entirely rather than persist an empty object, so raffles without a timer
  // keep looking exactly as they did before this feature existed.
  if (!enabled && startAt === undefined && endAt === undefined) return undefined

  return cleanObject({ enabled, startAt, endAt, timezone, endAction })
}

const normalizeScheduleTime = (value: unknown) => {
  const text = typeof value === "string" ? value.trim() : ""
  // <input type="time"> can yield "HH:mm:ss" - the schedule engine only stores "HH:mm".
  return /^\d{2}:\d{2}(:\d{2})?$/.test(text) ? text.slice(0, 5) : text
}

// Persists the weekly (recurring) schedule block. Returns undefined for a raffle that never
// configured one, so one-time raffles keep looking exactly as before this feature existed.
export const normalizeWeeklyScheduleForSave = (weeklySchedule: unknown) => {
  if (!isPlainObject(weeklySchedule)) return undefined

  const weekdays = Array.isArray(weeklySchedule.weekdays)
    ? Array.from(
        new Set(
          weeklySchedule.weekdays
            .map((day) => Number(day))
            .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7),
        ),
      ).sort((a, b) => a - b)
    : []
  const enabled = Boolean(weeklySchedule.enabled)
  const repeatIndefinitely = Boolean(weeklySchedule.repeatIndefinitely)

  const normalized = cleanObject({
    enabled,
    paused: enabled && Boolean(weeklySchedule.paused),
    weekdays,
    publishTime: normalizeScheduleTime(weeklySchedule.publishTime) || undefined,
    endTime: normalizeScheduleTime(weeklySchedule.endTime) || undefined,
    timezone: optionalTrimmedString(weeklySchedule.timezone) || defaultTimezone(),
    startDate: optionalTrimmedString(weeklySchedule.startDate),
    repeatIndefinitely,
    endDate: repeatIndefinitely ? undefined : optionalTrimmedString(weeklySchedule.endDate),
  }) as Record<string, unknown> | undefined

  // Never configured (toggle untouched, nothing filled in): omit entirely.
  if (!enabled && !weekdays.length && !weeklySchedule.publishTime && !weeklySchedule.startDate) {
    return undefined
  }
  return normalized
}

// Cancelling (toggle off) hands Raffle Date / Visible From back to the admin: the scheduler
// overwrote them on every run, so the values captured the first time it took over are restored.
// Participations, winners and results are never touched by either direction. The state block is
// reset (not deleted) because `updateDoc` ignores undefined, so a delete would never persist.
export const applyWeeklyScheduleCancellation = (raffle: any) => {
  const state = raffle?.weeklyScheduleState
  if (!isPlainObject(state) || state.originalsCaptured !== true) return raffle
  if (raffle?.weeklySchedule?.enabled === true) return raffle

  const next = { ...raffle }
  if (state.originalRaffleDate !== undefined && state.originalRaffleDate !== null) {
    next.raffleDate = state.originalRaffleDate
  }
  next.visibleFrom = state.originalVisibleFrom ?? null
  next.weeklyScheduleState = { originalsCaptured: false }
  return next
}

// Cross-field validation RJSF's schema alone can't express (comparing two sibling number
// fields). Mirrors the spec's "no permitir Timer activo incompleto" / "fin posterior a inicio"
// rules: only enforced while the Timer toggle is on, so a raffle with the timer off can still
// be saved with empty/partial dates. Wired into RaffleEditView.tsx's <Form customValidate>.
export const validateRaffleForm = (formData: any, errors: any) => {
  const timer = formData?.timer
  if (timer?.enabled) {
    if (!timer.startAt) {
      errors.timer.startAt.addError("Required while the timer is enabled")
    }
    if (!timer.endAt) {
      errors.timer.endAt.addError("Required while the timer is enabled")
    }
    if (timer.startAt && timer.endAt && timer.endAt <= timer.startAt) {
      errors.timer.endAt.addError("End date/time must be after the start date/time")
    }
  }

  // Only enforced while the schedule is on, so a one-time raffle (or a cancelled schedule with
  // leftover fields) can always be saved.
  if (formData?.weeklySchedule?.enabled) {
    const scheduleErrors = validateWeeklySchedule(
      normalizeWeeklyScheduleForSave(formData.weeklySchedule),
    )
    scheduleErrors.forEach((message) => errors.weeklySchedule?.addError(message))
  }

  // A schema `pattern` would reject an empty value too, but an empty allowedZip is
  // valid - it means "no restriction" (unchanged existing behavior).
  const allowedZip = typeof formData?.allowedZip === "string" ? formData.allowedZip.trim() : ""
  if (allowedZip && !ZIP_CODE_PATTERN.test(allowedZip)) {
    errors.allowedZip.addError("Must be a 5-digit ZIP code, or left empty for no restriction")
  }

  return errors
}

export const prepareRaffleForSave = (raffle: any) => {
  if (!raffle) return raffle

  const normalized = normalizeRaffleForForm(raffle)
  const shouldUseGoalMode = isGoalRaffleFormData(normalized)
  const nextRaffle = { ...normalized }
  // Always persist `allowedZip` as a trimmed string (`""` when cleared or omitted) so
  // `cleanObject`/`removeUndefinedFields` never strip it and Firestore's `updateDoc`
  // overwrites any previously stored ZIP code restriction.
  nextRaffle.allowedZip = normalizeAllowedZipForSave(nextRaffle.allowedZip)
  nextRaffle.ticketDesign = normalizeTicketDesignForSave(nextRaffle.ticketDesign)
  nextRaffle.publishNotification = normalizePublishNotificationForSave(
    nextRaffle.publishNotification,
  )
  nextRaffle.timer = normalizeTimerForSave(nextRaffle.timer)
  // `weeklySchedule` is only written when configured; a cancelled schedule stays as
  // `{ enabled: false, ... }` so Firestore overwrites the previously stored enabled schedule.
  nextRaffle.weeklySchedule = normalizeWeeklyScheduleForSave(nextRaffle.weeklySchedule)
  Object.assign(nextRaffle, applyWeeklyScheduleCancellation(nextRaffle))

  if (!shouldUseGoalMode) {
    delete nextRaffle.goalSettings
    nextRaffle.raffleMode = "standard"
    return cleanObject(syncLegacyRaffleMediaFields(nextRaffle))
  }

  nextRaffle.type = GOAL_RAFFLE_TYPE
  nextRaffle.raffleMode = "goal"
  nextRaffle.goalSettings = normalizeGoalSettingsForSave(nextRaffle.goalSettings)

  return cleanObject(syncLegacyRaffleMediaFields(nextRaffle))
}
